import React, { useEffect, useState } from 'react';
import { Card, Typography, Form, Input, Select, Button, theme } from 'antd';
import { IconLogout } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import AppLayout from '../../components/Layouts/Layout';
import AvatarUpload from '../../components/AvatarUpload';
import PhoneNumberInput from '../../components/PhoneNumberInput';
import { getCountries, ICountryResponse } from '../../services/LocaleService';
import { createUserProfile } from '../../services/UserProfileService';
import { useUserProfile } from '../../contextProviders/UserProfileProvider';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { APP_LANGUAGE_OPTIONS, resolveAppLanguage } from '../../constants/appLanguages';

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface CompleteProfileFormValues {
  displayName: string;
  photoUrl?: string;
  phone: string;
  alpha2Code: string;
  preferredLanguage: string;
}

/**
 * Registration's step 2 for this app -- collects the HeavyDutyMachinery-specific
 * identity (see UserProfile's backend doc comment) right after
 * AuthorizationApi's bare email+password registration. ProtectedRoute
 * redirects here whenever UserProfileProvider reports no profile yet,
 * same pattern as AgeVerification.tsx/PENDING_AGE_VERIFICATION_KEY.
 */
const CompleteProfile: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { refresh } = useUserProfile();
  const [form] = Form.useForm<CompleteProfileFormValues>();
  const photoUrl = Form.useWatch('photoUrl', form);
  const [countries, setCountries] = useState<ICountryResponse[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getCountries()
      .then(setCountries)
      .catch(() => message.error(translate(t, TRANSLATIONS.REGISTRATION_LOAD_COUNTRIES_ERROR)))
      .finally(() => setLoadingCountries(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: CompleteProfileFormValues) => {
    setSubmitting(true);
    try {
      const country = countries.find((c) => c.alpha2Code === values.alpha2Code);
      await createUserProfile({
        displayName: values.displayName,
        photoUrl: values.photoUrl,
        phone: values.phone,
        country: country?.name ?? '',
        countryAlpha2Code: values.alpha2Code,
        preferredLanguage: values.preferredLanguage,
      });
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.COMPLETE_PROFILE_ERROR_FALLBACK));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          {translate(t, TRANSLATIONS.COMPLETE_PROFILE_TITLE)}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.COMPLETE_PROFILE_SUBTITLE)}
        </Paragraph>

        <Card style={{ borderRadius: 20, border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            // Defaults to whatever the interface is already showing (from
            // i18next-browser-languagedetector) rather than hardcoding "en" --
            // there's no account language yet at this point, so the current
            // UI language is the only real signal of the user's preference.
            initialValues={{ preferredLanguage: resolveAppLanguage(i18n.resolvedLanguage ?? i18n.language) }}
          >
            <Form.Item label={translate(t, TRANSLATIONS.PROFILE_PHOTO_URL_LABEL)}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <AvatarUpload
                  value={photoUrl}
                  onChange={(url) => form.setFieldsValue({ photoUrl: url })}
                  disabled={submitting}
                />
              </div>
            </Form.Item>
            <Form.Item name="photoUrl" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              label={translate(t, TRANSLATIONS.PROFILE_DISPLAY_NAME_LABEL)}
              name="displayName"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.PROFILE_DISPLAY_NAME_LABEL) }]}
            >
              <Input disabled={submitting} />
            </Form.Item>

            <Form.Item
              label={translate(t, TRANSLATIONS.COMPLETE_PROFILE_PHONE_LABEL)}
              name="phone"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.COMPLETE_PROFILE_PHONE_LABEL) }]}
            >
              <PhoneNumberInput disabled={submitting} />
            </Form.Item>

            <Form.Item
              label={translate(t, TRANSLATIONS.COUNTRY)}
              name="alpha2Code"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.COUNTRY_MESSAGE) }]}
            >
              <Select
                showSearch
                loading={loadingCountries}
                placeholder={translate(t, TRANSLATIONS.COUNTRY_PLACEHOLDER)}
                disabled={submitting}
                filterOption={(input, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {countries.map((country) => (
                  <Option key={country.alpha2Code} value={country.alpha2Code}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={translate(t, TRANSLATIONS.MY_ACCOUNT_LANGUAGE_LABEL)} name="preferredLanguage">
              <Select
                disabled={submitting}
                options={APP_LANGUAGE_OPTIONS}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" shape="round" htmlType="submit" block size="large" loading={submitting}>
                {translate(t, TRANSLATIONS.COMPLETE_PROFILE_SUBMIT)}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* This screen is a mandatory gate (ProtectedRoute redirects every
            other route back here until a profile exists) -- on mobile,
            AppHeader collapses to just the logo + bell and MobileBottomNav's
            tabs all bounce back here too, so without this link someone who
            wants to abandon and sign in as a different account has no way
            out short of clearing storage by hand. */}
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Button type="text" icon={<IconLogout size={16} />} onClick={logout} style={{ color: token.colorTextSecondary }}>
            {translate(t, TRANSLATIONS.LOGOUT)}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
};

export default CompleteProfile;
